import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Chip, LinearProgress, Stack, Tooltip, Typography } from '@mui/material';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import { grey } from '@mui/material/colors';
import { navigateTabsWithQueries } from '../types/cutterState';
import { getColorFromPercentage } from '../utils/colorUtils';

interface CutterMetricsStackProps {
    commercialVersion: string | null;
    SoftwareUpdateIndex: number | null;
    ConnectionRate: number | null;
    sx?: React.ComponentProps<typeof Stack>['sx'];
}

const StatBar: React.FC<{
    value: number | null;
    color: 'success' | 'warning' | 'error' | 'info';
    icon: React.ReactNode;
    label: string;
}> = ({ value, color, icon, label }) => {
    if (value === null) return null;
    const percentage = Math.min(100, Math.max(0, Math.round(value)));

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.4, width: '100%' }}>
            <Box sx={{ color: `${color}.main`, display: 'flex', fontSize: 11 }}>{icon}</Box>
            <Tooltip title={`${label}: ${percentage}%`} placement="right" arrow>
                <LinearProgress variant="determinate" value={percentage} color={color}
                    sx={{ flex: 1, height: 5, borderRadius: 3 }} />
            </Tooltip>
            <Typography sx={{ fontSize: '9px', minWidth: 26, textAlign: 'right' }}>{percentage}%</Typography>
        </Box>
    );
};

export const CutterMetricsStack: React.FC<CutterMetricsStackProps> = ({ commercialVersion, SoftwareUpdateIndex, ConnectionRate, sx }) => {
    const navigate = useNavigate();
    const connectionColor = ConnectionRate === null ? 'info' : ConnectionRate >= 80 ? 'success' : ConnectionRate >= 50 ? 'warning' : 'error';
    const updateColor = SoftwareUpdateIndex === null ? 'info' : SoftwareUpdateIndex >= 80 ? 'success' : SoftwareUpdateIndex >= 50 ? 'warning' : 'error';

    return (
        <Stack direction="column" spacing={1} marginTop="1em" alignItems="center" alignSelf="center" justifyContent="flex-start" sx={sx}>
            {commercialVersion && (
                <Chip
                    label={commercialVersion}
                    onClick={(event) => {
                        event.stopPropagation();
                        navigateTabsWithQueries(navigate, 'search', commercialVersion);
                    }}
                    sx={{
                        backgroundColor: SoftwareUpdateIndex === null ? 'action.disabledBackground' : getColorFromPercentage(SoftwareUpdateIndex),
                        color: (theme) => theme.palette.mode === 'light' ? 'inherit' : '#252525',
                        paddingX: 2, paddingY: 1, height: '12px', fontSize: '0.65rem', fontWeight: 'bold'
                    }}
                    size="small"
                />
            )}
            <StatBar value={ConnectionRate} color={connectionColor} icon={<SignalCellularAltIcon fontSize="inherit" />} label="Connection rate" />
            <StatBar value={SoftwareUpdateIndex} color={updateColor} icon={<SystemUpdateAltIcon fontSize="inherit" />} label="SW update index" />
        </Stack>
    );
};

export default CutterMetricsStack;